'use client';

import React, { useState } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { Button } from '@tranquility-ai/theme-components/button';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';

/** Figma stacked modal section well (`ink/095`, 16px radius, 8px bottom padding). */
export const STACKED_SOURCES_SECTION_SHELL_CLASS =
  'box-border flex w-full min-w-0 max-w-full flex-col rounded-2xl bg-[var(--color-ink-095,#091523)] pb-2';

/** Vertical gap between Displayed Media, Displayed Sources, and Available Sources wells. */
export const STACKED_SOURCES_SECTIONS_GAP_CLASS = 'gap-[var(--spacing-xxlarge,24px)]';

/** Stacked Displayed Sources: 5 rows at 52px + 4×4px (`gap-1`), then scroll (TR-2924). */
export const STACKED_DISPLAYED_SOURCES_LIST_CLASS =
  'box-border flex flex-col gap-1 w-full p-0 m-0 list-none max-h-[calc(5*52px+4*4px)] overflow-y-auto custom-scrollbar2';

/** Stacked Displayed Media: 2 tile rows (140px + label) + 1 row gap, then scroll (TR-3055).
 *  Top padding (`--spacing-large`) keeps the first row's selection ring off the
 *  section header (TR-3132). */
export const STACKED_DISPLAYED_MEDIA_STRIP_CLASS =
  'box-border flex w-full max-w-full max-h-[calc(2*(140px+4px+16px)+16px+var(--spacing-large,8px))] flex-wrap content-start gap-[var(--spacing-xlarge,16px)] overflow-y-auto px-2 pt-[var(--spacing-large,8px)] custom-scrollbar2';

type SourcesSectionHeaderProps = {
  title: string;
  /** Rendered as "Title (n)" when provided. */
  count?: number;
  tooltip: string;
  actionLabel?: string;
  onAction?: () => void;
};

/**
 * The Figma ".ResultReturns" row: a muted section label with an info affordance
 * on the left and a single brand-text action (Clear All / Add All) on the right.
 */
export function SourcesSectionHeader({
  title,
  count,
  tooltip,
  actionLabel,
  onAction,
}: SourcesSectionHeaderProps) {
  const t = useTimePilotT();
  const [infoOpen, setInfoOpen] = useState(false);
  const sectionLabel = count === undefined ? title : `${title} (${count})`;
  const aboutLabel = t('sources.about', { title });

  return (
    <div className="box-border flex w-full items-center justify-between py-2 pl-[10px] pr-1">
      <div className="flex min-w-0 flex-1 items-center gap-[2px]">
        <span className="truncate text-[14px] font-medium leading-5 text-[color:var(--text-placeholder)]">
          {sectionLabel}
        </span>
        <Popover
          isOpen={infoOpen}
          onOpenChange={setInfoOpen}
          placement="top"
          variant="inverted"
          maxWidth="16.5rem"
          aria-label={aboutLabel}
          content={<p className="m-0 text-sm leading-5">{tooltip}</p>}
        >
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            aria-label={aboutLabel}
            aria-haspopup="dialog"
            className="shrink-0"
            onClick={() => setInfoOpen((open) => !open)}
          >
            <InfoIcon size={16} className="text-[color:var(--text-placeholder)]" />
          </Button>
        </Popover>
      </div>
      {actionLabel && onAction && (
        <Button
          type="button"
          size="sm"
          style="text"
          color="accent"
          background="inverse"
          className="shrink-0 text-sm font-medium"
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

type SourcesEmptyStateProps = {
  children: React.ReactNode;
  /** Square 140px chip for the media strip; full-width bar for the lists. */
  variant?: 'tile' | 'bar';
};

/** Figma "Empty State Chip" — an ink/100 well with centered muted copy. */
export function SourcesEmptyState({ children, variant = 'bar' }: SourcesEmptyStateProps) {
  return (
    <div
      className={`box-border flex items-center justify-center rounded-lg bg-[var(--color-ink-100,#000000)] text-center text-[14px] font-medium leading-4 text-[color:var(--text-muted,#6888a6)] ${
        variant === 'tile' ? 'size-[140px] shrink-0 px-4' : 'w-full p-4'
      }`}
    >
      {children}
    </div>
  );
}
